/**
 * Creates an instance of axios that includes authorization headers and custom headers.
 * @param token The authentication token to include in the request headers.
 * @returns An axios instance with interceptors for adding headers and handling errors.
 */
import axios, { AxiosRequestConfig } from 'axios';

import { ApplicationType, x_app_type } from '../../applicaiton';

/**
 * Creates an instance of axios that includes authorization headers and custom headers.
 * @param token The authentication token to include in the request headers.
 * @returns An axios instance with interceptors for adding headers and handling errors.
 */
const axiosAuthInstance = (token: string) => {
  /**
   * Axios instance configured with a base URL.
   */
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
  });

  /**
   * Adds authorization and custom headers to the request config.
   * @param apiConfig The original API request configuration.
   * @returns A new API request configuration with updated headers.
   */
  axiosInstance.interceptors.request.use(
    async (apiConfig: AxiosRequestConfig) => {
      const newHeaders = {
        ...(apiConfig.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        [x_app_type]: ApplicationType.ProfessionalWeb,
      };
      return { ...apiConfig, headers: newHeaders };
    },
  );

  /**
   * Handles successful responses by optionally doing something with the response data.
   * @param response The original API response object.
   * @returns A modified API response object.
   */
  axiosInstance.interceptors.response.use(
    function (response) {
      // Optional: Do something with response data
      return response;
    },
    /**
     * Handles errors by logging the error to the console and returning a rejected promise.
     * @param error The original API error object.
     * @returns A rejected promise that can be handled at the call-site.
     */
    function (error) {
      // Do whatever you want with the response error here:

      console.log(error);
      // But, be SURE to return the rejected promise, so the caller still has
      // the option of additional specialized handling at the call-site:
      return Promise.reject(error);
    },
  );

  return axiosInstance;
};

export default axiosAuthInstance;
